<template>
  <div>
    <div class="d-flex flex-wrap align-baseline mb-4" style="gap: 6px 14px">
      <h2 class="ct-title">{{ $t("finance_tab_contract") }}</h2>
      <span class="ct-sub">{{ canEdit ? $t("finance_contract_admin_hint") : $t("finance_contract_readonly_hint") }}</span>
    </div>

    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />

    <empty-state
      v-if="!restaurantId"
      icon="mdi-store-outline"
      :title="$t('finance_select_restaurant')"
    />

    <template v-else>
      <!-- Ugovor i njegov rok -->
      <v-card outlined class="pa-5 mb-4">
        <div v-if="contract" class="d-flex flex-wrap align-center" style="gap: 12px 24px">
          <div>
            <div class="ct-label">{{ $t("finance_contract_label") }}</div>
            <div class="ct-contract">
              {{ $t("finance_contract_number_short", { number: contract.contract_number }) }}
              · {{ $t("finance_contract_valid") }} {{ localeDate(contract.valid_from) }} –
              {{ contract.valid_to ? localeDate(contract.valid_to) : $t("contract_no_end_date_short") }}
            </div>
            <div class="ct-label mt-1">
              <template v-if="contract.contract_date">
                {{ $t("finance_contract_signed", { date: localeDate(contract.contract_date) }) }}
              </template>
              <template v-if="contract.contract_date && contract.document_url"> · </template>
              <a v-if="contract.document_url" :href="contract.document_url" target="_blank" rel="noopener">
                {{ $t("finance_contract_document_pdf") }}
              </a>
            </div>
          </div>
          <v-spacer />
          <div v-if="contractPill" class="ct-pill" :class="`ct-pill-${contractPill.tone}`">
            <v-icon small :color="contractPill.tone === 'error' ? '#a12a2a' : '#8a5a00'">mdi-alert-outline</v-icon>
            {{ contractPill.text }}
          </div>
        </div>
        <div v-else class="text-body-2 grey--text text--darken-1">
          {{ $t("finance_no_contract") }}
          <v-chip v-if="isAdmin" x-small color="error" text-color="white" class="ml-2">
            {{ $t("finance_missing") }}
          </v-chip>
        </div>
      </v-card>

      <!-- Usluge, tarife, dinamika i keš model -->
      <v-card outlined class="mb-4 ct-services">
        <div class="px-5 pt-5 pb-3 text-subtitle-1 font-weight-bold">{{ $t("finance_services_and_tariffs") }}</div>
        <v-simple-table>
          <thead>
            <tr>
              <th>{{ $t("finance_service_label") }}</th>
              <th>{{ $t("finance_tariff") }}</th>
              <th>{{ $t("price_valid_from") }}</th>
              <th>{{ $t("finance_payout_dynamics_long") }}</th>
              <th>{{ $t("finance_cash_model") }}</th>
              <th>{{ $t("finance_col_status") }}</th>
              <th v-if="canEdit" />
            </tr>
          </thead>
          <tbody>
            <tr v-if="!services.length">
              <td :colspan="canEdit ? 7 : 6" class="text-center grey--text py-6">
                {{ loading ? $t("finance_loading") : $t("finance_no_services") }}
              </td>
            </tr>
            <tr
              v-for="item in services"
              :key="item.service_id"
              class="ct-row"
              :class="{ 'ct-row-inactive': !item.is_active, 'ct-row-selected': item.service_id === selectedServiceId }"
              @click="selectedServiceId = item.service_id"
            >
              <td class="ct-name">{{ translate(item.name) }}</td>
              <td>
                <template v-if="item.is_active && item.pricing">
                  {{ translate(tariffLabel(item.pricing)) }} ·
                  <strong>{{ tariffValue(item.pricing) }}</strong>
                  <v-tooltip v-if="item.pricing.is_verified === false" top max-width="320">
                    <template #activator="{ on, attrs }">
                      <v-icon x-small color="warning" v-bind="attrs" v-on="on">mdi-alert-outline</v-icon>
                    </template>
                    <span>{{ $t("pricing_not_verified_desc") }}</span>
                  </v-tooltip>
                </template>
                <span v-else class="grey--text">—</span>
              </td>
              <td>
                <template v-if="item.is_active && item.pricing">{{ localeDate(item.pricing.valid_from) }}</template>
                <span v-else class="grey--text">—</span>
              </td>
              <td>
                <template v-if="!item.is_active"><span class="grey--text">—</span></template>
                <template v-else-if="scheduleOf(item)">{{ scheduleOf(item) }}</template>
                <template v-else>
                  <strong class="ct-warn">{{ $t("finance_schedule_not_agreed") }}</strong>
                  <v-chip v-if="isAdmin" x-small color="error" text-color="white" class="ml-1">!</v-chip>
                </template>
              </td>
              <td>
                <template v-if="item.is_active && item.uses_cash_flow_model && item.contract && item.contract.cash_flow_model">
                  {{ cashModelLabel(item.contract.cash_flow_model) }}
                </template>
                <span v-else class="grey--text">—</span>
              </td>
              <td>
                <span class="ct-state" :class="item.is_active ? 'ct-state-on' : 'ct-state-off'">
                  {{ item.is_active ? $t("finance_status_active") : $t("finance_status_inactive") }}
                </span>
              </td>
              <td v-if="canEdit" class="text-right text-no-wrap">
                <template v-if="item.is_active">
                  <v-btn x-small outlined color="primary" @click.stop="openSetPrice(item)">
                    {{ $t("change_price") }}
                  </v-btn>
                  <v-btn
                    v-if="item.pricing && item.pricing.is_verified === false"
                    x-small
                    text
                    color="warning"
                    :loading="verifyingId === item.pricing.id"
                    @click.stop="verifyPrice(item.pricing)"
                  >
                    {{ $t("confirm_price") }}
                  </v-btn>
                </template>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
        <p class="ct-note px-5 py-3 mb-0">{{ $t("finance_tariff_types_note") }}</p>
      </v-card>

      <v-row>
        <!-- Istorija cijena za izabranu uslugu -->
        <v-col cols="12" md="6">
          <v-card outlined class="pa-5 fill-height">
            <div class="text-subtitle-1 font-weight-bold mb-3">
              {{ $t("finance_price_history_title", { service: selectedService ? translate(selectedService.name) : "" }) }}
            </div>
            <empty-state v-if="!selectedHistory.length" icon="mdi-history" :title="$t('finance_no_price_history')" />
            <div v-else>
              <div
                v-for="entry in selectedHistory"
                :key="entry.id"
                class="hist-row"
                :class="{ 'hist-old': !entry.is_current }"
              >
                <span class="hist-period">
                  {{ localeDate(entry.valid_from) }} –
                  {{ entry.valid_to ? localeDate(entry.valid_to) : "" }}
                </span>
                <span :class="{ 'font-weight-bold': entry.is_current }">{{ translate(entry.opis) }}</span>
                <span class="hist-by">
                  <v-chip v-if="entry.is_verified === false" x-small color="warning" text-color="white" class="mr-1">
                    {{ $t("finance_not_verified") }}
                  </v-chip>
                  <template v-if="entry.created_by">{{ $t("finance_confirmed_by_name", { name: translate(entry.created_by) }) }}</template>
                  <template v-else>—</template>
                </span>
              </div>
            </div>
            <p class="ct-note mt-3 mb-0">{{ $t("finance_price_history_note") }}</p>
          </v-card>
        </v-col>

        <!-- Kako se računa isplata -->
        <v-col cols="12" md="6">
          <v-card outlined class="pa-5 fill-height">
            <div class="text-subtitle-1 font-weight-bold mb-3">{{ $t("finance_how_payout_title") }}</div>
            <ol class="ct-steps">
              <li>{{ $t("finance_how_payout_1") }}</li>
              <li>{{ $t("finance_how_payout_2") }}</li>
              <li>{{ $t("finance_how_payout_3") }}</li>
              <li>{{ $t("finance_how_payout_4") }}</li>
            </ol>
            <a class="mt-3 d-inline-block" @click="$emit('navigate', 'settlements')">
              {{ $t("finance_view_settlements") }} →
            </a>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <set-price-dialog
      v-if="canEdit && priceService"
      v-model="priceOpen"
      :restaurant-id="restaurantId"
      :service-id="priceService.service_id"
      :paying-types="payingTypes"
      :current-price="priceCurrent"
      @saved="load"
    />
  </div>
</template>

<script>
import EmptyState from "@/components/global/EmptyState.vue";
import SetPriceDialog from "@/components/contracts/SetPriceDialog.vue";
import hasSnackbar from "@/mixins/hasSnackbar";
import contractsService from "@/services/contracts";
import pricingService from "@/services/pricing";
import restaurantService from "@/services/restaurant";
import hasFinanceContext from "./hasFinanceContext";
import { CASH_FLOW_MODEL_LABEL_KEYS, PAYOUT_SCHEDULE_LABEL_KEYS } from "./constants";

// Ugovor i cijene: ono što je ugovoreno i od kad važi. Restoran gleda, admin mijenja
// kroz postojeće dijaloge cjenovnika (SetPriceDialog / verify).
export default {
  name: "FinanceScreenContract",

  components: { EmptyState, SetPriceDialog },

  mixins: [hasSnackbar, hasFinanceContext],

  // Dijeli se sa ostalim ekranima kroz finance.vue; ovaj ekran period ne koristi.
  props: {
    period: { type: Object, default: null },
  },

  data: () => ({
    loading: false,
    contract: null,
    services: [],
    options: {},
    history: [],
    canEditServer: true,
    selectedServiceId: null,
    priceOpen: false,
    priceService: null,
    verifyingId: null,
  }),

  computed: {
    canEdit() {
      return this.isAdmin && this.canEditServer;
    },
    selectedService() {
      return this.services.find((s) => s.service_id === this.selectedServiceId) || null;
    },
    selectedHistory() {
      return this.history
        .filter((h) => h.service_id === this.selectedServiceId)
        .sort((a, b) => String(b.valid_from).localeCompare(String(a.valid_from)));
    },
    payingTypes() {
      return Object.entries(this.options.paying_types || {}).map(([value, text]) => ({
        value: Number(value),
        text,
      }));
    },
    priceCurrent() {
      return this.history.find((h) => h.service_id === this.priceService?.service_id && h.is_current) || null;
    },
    // Rok ugovora: istekao = crveno ("i dalje se obračunava"), inače svijetlo žuto sa brojem dana.
    contractPill() {
      const days = this.contract?.dana_do_isteka;
      if (days === null || days === undefined) return null;
      if (this.contract.status === "ISTEKAO" || days < 0) {
        return {
          tone: "error",
          text: `${this.$t("contract_expired_days_ago", { count: Math.abs(days) })} · ${this.$t("still_being_billed")}`,
        };
      }
      return { tone: "warning", text: this.$t("finance_expires_in_days", { count: days }) };
    },
  },

  watch: {
    restaurantId: "load",
  },

  created() {
    this.load();
  },

  methods: {
    numberText(value) {
      return Number(value).toLocaleString("sr-RS", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    },

    // "Procenat od prodaje" - naziv tipa tarife iz options.paying_types (ne ručno sastavljen)
    tariffLabel(pricing) {
      return (this.options.paying_types || {})[pricing.paying_type] || "";
    },

    tariffValue(pricing) {
      if (pricing.amount == null) return pricing.opis || "—";
      return Number(pricing.paying_type) === 2
        ? `${this.numberText(pricing.amount)} %`
        : this.money(pricing.amount);
    },

    scheduleOf(service) {
      const contract = service.contract;
      if (!contract || !contract.payout_schedule) return "";
      if (contract.payout_schedule === "THRESHOLD" && contract.payout_threshold_amount != null) {
        return this.$t("finance_schedule_threshold_amount", {
          amount: this.money(contract.payout_threshold_amount),
        });
      }
      const key = PAYOUT_SCHEDULE_LABEL_KEYS[contract.payout_schedule];
      return key ? this.$t(key) : contract.payout_schedule;
    },

    cashModelLabel(model) {
      const key = CASH_FLOW_MODEL_LABEL_KEYS[model];
      return key ? this.$t(key) : model;
    },

    openSetPrice(service) {
      this.priceService = service;
      this.$nextTick(() => {
        this.priceOpen = true;
      });
    },

    async verifyPrice(pricing) {
      this.verifyingId = pricing.id;
      try {
        await pricingService.verifyRestaurantPrice(this.restaurantId, pricing.id);
        this.showSuccessSnackbar(this.$t("price_confirmed_successfully"));
        await this.load();
      } catch (error) {
        this.showErrorSnackbar(error?.response?.data?.message || this.$t("finance_contract_load_error"));
      } finally {
        this.verifyingId = null;
      }
    },

    async load() {
      if (!this.restaurantId) return;
      this.loading = true;
      try {
        const [contractsRes, servicesRes, pricingRes] = await Promise.all([
          contractsService.getRestaurantContracts(this.restaurantId).catch(() => null),
          restaurantService.getRestaurantServices(this.restaurantId),
          pricingService.getRestaurantPricing(this.restaurantId).catch(() => null),
        ]);
        // Prvi ugovor u listi je aktivan (backend sortira aktivan prvi, pa ostali unazad)
        this.contract = contractsRes?.data?.contracts?.[0] || null;

        const servicesData = servicesRes.data?.data || servicesRes.data || {};
        this.services = servicesData.services || [];
        this.options = servicesData.options || {};
        this.canEditServer = servicesData.can_edit !== false;

        const pricingData = pricingRes?.data?.data || pricingRes?.data || {};
        this.history = pricingData.history || [];
        if (!this.options.paying_types && pricingData.options) {
          this.options = { ...this.options, ...pricingData.options };
        }

        const selectable = this.services.filter((s) => s.is_active);
        if (!this.selectedService || !this.selectedService.is_active) {
          this.selectedServiceId = selectable.length ? selectable[0].service_id : null;
        }
      } catch (error) {
        this.showErrorSnackbar(this.$t("finance_contract_load_error"));
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.ct-title {
  font-size: 1.6rem;
  font-weight: 800;
  margin: 0;
}

.ct-sub {
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.6);
}

.ct-label {
  font-size: 0.82rem;
  color: rgba(0, 0, 0, 0.6);
}

.ct-contract {
  font-size: 1.35rem;
  font-weight: 800;
}

.ct-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  padding: 10px 16px;
  font-weight: 700;
  font-size: 0.9rem;
}

.ct-pill-warning {
  background: #fdf0d5;
  color: #8a5a00;
}

.ct-pill-error {
  background: #fbe3e3;
  color: #a12a2a;
}

.ct-services ::v-deep thead th {
  background: #f6f6f4;
  font-size: 0.78rem !important;
  font-weight: 700 !important;
  color: rgba(0, 0, 0, 0.6) !important;
}

.ct-services ::v-deep td {
  font-size: 0.92rem !important;
  height: 58px !important;
}

.ct-row {
  cursor: pointer;
}

.ct-row-selected {
  box-shadow: inset 3px 0 0 #ff6e3a;
}

.ct-row-inactive td {
  color: rgba(0, 0, 0, 0.5);
}

.ct-name {
  font-weight: 700;
}

.ct-warn {
  color: #8a5a00;
}

.ct-state {
  display: inline-block;
  border-radius: 999px;
  padding: 3px 12px;
  font-size: 0.76rem;
  font-weight: 700;
}

.ct-state-on {
  background: #e2f3e8;
  color: #1b6e3a;
}

.ct-state-off {
  background: #eeeeec;
  color: rgba(0, 0, 0, 0.6);
}

.ct-note {
  font-size: 0.78rem;
  color: rgba(0, 0, 0, 0.6);
}

.hist-row {
  display: grid;
  grid-template-columns: 170px 1fr auto;
  gap: 10px;
  padding: 11px 0;
  border-top: 1px solid #eceef2;
  font-size: 0.9rem;
}

.hist-old {
  color: rgba(0, 0, 0, 0.55);
}

.hist-by {
  font-size: 0.82rem;
  color: rgba(0, 0, 0, 0.55);
  text-align: right;
}

.ct-steps {
  padding-left: 20px;
  font-size: 0.9rem;
  line-height: 1.7;
  color: rgba(0, 0, 0, 0.75);
}

.ct-steps li {
  margin-bottom: 6px;
}
</style>
